import React, { useState, useRef, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { apiCall, API_ENDPOINTS } from '../../utils/api';
import './ChatWidget.css';

const ChatWidget = ({ isAuthenticated, externalOpen, onClose }) => {
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Handle external open trigger
  useEffect(() => {
    if (externalOpen) {
      setIsOpen(true);
      if (onClose) {
        // Call onClose after opening to reset the external trigger
        setTimeout(() => onClose(), 100);
      }
    }
  }, [externalOpen, onClose]);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollingInterval = useRef(null);

  // Initialize chat session and load messages
  useEffect(() => {
    if (isAuthenticated && user?.id && isOpen) {
        initializeChat();
    }
    
    return () => {
        if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
        }
    };
  }, [isAuthenticated, user, isOpen]);

  const initializeChat = async () => {
    if (!user?.id) return;
    
    try {
        // Start/Get chat session
        const response = await apiCall(API_ENDPOINTS.START_CHAT, 'POST', {
            action: 'start_chat',
            user_id: user.id,
            subject: 'Support Request'
        });

        if (response.success && response.chat_id) {
            setChatId(response.chat_id);
            fetchMessages(response.chat_id);
            
            // Start polling
            if (pollingInterval.current) clearInterval(pollingInterval.current);
            pollingInterval.current = setInterval(() => fetchMessages(response.chat_id), 3000);
        }
    } catch (error) {
        console.error('Error initializing chat:', error);
    }
  };

  const fetchMessages = async (currentChatId) => {
    if (!currentChatId) return;
    
    try {
        const response = await apiCall(API_ENDPOINTS.CHAT_MESSAGES(currentChatId));
        if (response.success) {
            // Map DB format to component format
            // DB: message, sender_type, created_at
            // Component: text, sender, timestamp
            const formattedMessages = response.data.map(msg => ({
                id: msg.id,
                text: msg.message,
                sender: msg.sender_type,
                timestamp: msg.created_at
            })).reverse(); // API returns DESC, we likely need ASC for chat view
            
            setMessages(formattedMessages);
        }
    } catch (error) {
        console.error('Error fetching messages:', error);
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || sending || !chatId) {
      return;
    }

    const messageText = inputMessage.trim();
    setSending(true);

    try {
        const response = await apiCall(API_ENDPOINTS.SEND_MESSAGE, 'POST', {
            action: 'send_message',
            chat_id: chatId,
            user_id: user.id,
            message: messageText,
            sender_type: 'user'
        });

        if (response.success) {
            setInputMessage('');
            fetchMessages(chatId);
        }
    } catch (error) {
        console.error('Error sending message:', error);
    } finally {
        setSending(false);
    }
  };


  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <button 
        className={`chat-widget-button ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        aria-label="Open chat"
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {!isOpen && messages.length > 0 && (
          <span className="chat-widget-badge">{messages.length}</span>
        )}
      </button>

      {isOpen && (
        <div className="chat-widget-container">
          <div className="chat-widget-header">
            <div className="chat-widget-header-info">
              <div className="chat-widget-avatar">ECOS</div>
              <div>
                <h3 className="chat-widget-title">ECOS Support</h3>
                <p className="chat-widget-subtitle">We're here to help</p>
              </div>
            </div>
            <button 
              className="chat-widget-close"
              onClick={handleToggle}
              aria-label="Close chat"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          <div className="chat-widget-messages">
            {messages.length === 0 ? (
              <div className="chat-widget-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="#98a2b3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p>Start a conversation with our support team</p>
              </div>
            ) : (
              messages.map((message) => (
                <div 
                  key={message.id} 
                  className={`chat-message ${message.sender === 'user' ? 'user' : 'admin'}`}
                >
                  <div className="chat-message-content">
                    <p>{message.text}</p>
                    <span className="chat-message-time">{formatTime(message.timestamp)}</span>
                  </div>
                </div>
              ))
            )}
            {sending && (
              <div className="chat-message admin">
                <div className="chat-message-content">
                  <div className="chat-typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form className="chat-widget-input-form" onSubmit={handleSendMessage}>
            <input
              ref={inputRef}
              type="text"
              className="chat-widget-input"
              placeholder="Type your message..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              disabled={sending}
            />
            <button
              type="submit"
              className="chat-widget-send"
              disabled={!inputMessage.trim() || sending}
              aria-label="Send message"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;

